import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { commonQueryKeys } from "./commonQueryKeys";

type Coord = Array<[number, number]>;
type Item = unknown;

const chunk = <T>(arr: T[], size: number) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));

const buildParam = (coords: Coord) => {
  const coordsString = coords.map((c) => c.join(",")).join(";");
  const radius = coords.map(() => "25").join(";");
  return `${coordsString}?overview=full&geometries=geojson&tidy=true&radiuses=${radius}`;
};

async function fetchBatch(coords: Coord) {
  const baseUrl = "https://router.project-osrm.org/match/v1/driving";
  const url = `${baseUrl}/${buildParam(coords)}`;
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Batches by 80 per call, runs them in parallel, and flattens.
 * Keeps order by mapping results back to the original chunk order.
 */
async function fetchBatched(coords: string, batchSize = 80): Promise<Item[]> {
  const coordsArray: Coord = coords.split(";").map((coord) => coord.split(",").map(Number) as [number, number]);
  const groups = chunk(coordsArray, batchSize);
  const results = await Promise.all(groups.map(fetchBatch));
  // If each batch returns a list corresponding to inputs, this preserves order:
  const flattenedResults = results.flat();

  return combineResults(flattenedResults);
}

const combineResults = (results: any[]) => {
  const combinedCoords: any = [];

  results.forEach((result: any) => {
    if (result?.matchings?.length) {
      if (result.matchings.length > 1) {
        const sorted = [...result.matchings].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
        if (sorted[0]?.geometry?.coordinates?.length) {
          combinedCoords.push(...reOrderCoords(sorted[0].geometry.coordinates));
        }
      } else if (result.matchings[0]?.geometry?.coordinates?.length) {
        combinedCoords.push(...reOrderCoords(result.matchings[0].geometry.coordinates));
      }
    }
  });

  return combinedCoords;
};

const reOrderCoords = (coords: any[]) => coords.map(([lng, lat]) => [lat, lng]);

export default function useTruckCoordsQuery(coords: string, options?: UseQueryOptions) {
  return useQuery({
    queryKey: commonQueryKeys.truckCoordinates(coords),
    queryFn: () => fetchBatched(coords, 90),
    enabled: options?.enabled && coords.length > 0,
    staleTime: 1000 * 60 * 60, // 60 minuites
    cacheTime: 1000 * 60 * 60, // 60 minuites
    ...options,
  });
}
